import { createRoot } from '@wordpress/element';
import DatePickerWrapper from './DatePickerWrapper.js';

const propelInit = function() {
class DateFilter {
	constructor(eventDateFilter) {
		this.eventDateFilter = eventDateFilter;

		this.datePickerButton = eventDateFilter.querySelector(
			'.filter-date__date-picker-button'
		);

		this.datePickerInput = eventDateFilter.querySelector(
			'.filter-date__date-picker-input'
		);

		this.datePicker = eventDateFilter.querySelector(
			'.filter-date__date-picker'
		);

		this.todayButton = eventDateFilter.querySelector(
			'.filter-date__today-button'
		);

		this.previousButton = eventDateFilter.querySelector(
			'.filter-date__nav-button--previous'
		);

		this.nextButton = eventDateFilter.querySelector(
			'.filter-date__nav-button--next'
		);

		this.monthView =
			eventDateFilter.classList.contains('filter-date--month');

		this.root = createRoot(this.datePicker);

		this.initialMonth =
			this.eventDateFilter.getAttribute('data-initial-month');

		this.initialYear =
			this.eventDateFilter.getAttribute('data-initial-year');

		this.currentMonth =
			this.eventDateFilter.getAttribute('data-current-month');

		this.currentYear =
			this.eventDateFilter.getAttribute('data-current-year');

		this.lastMonth =
			this.eventDateFilter.getAttribute('data-last-month') ??
			new Date().getFullYear() + 1;

		this.lastYear =
			this.eventDateFilter.getAttribute('data-last-year') ??
			new Date().getMonth();

		this.showPastEvents = this.eventDateFilter.getAttribute(
			'data-show-past-events'
		);

		if (this.datePickerButton) {
			this.datePickerButton.addEventListener(
				'click',
				this.toggleDatePicker.bind(this)
			);
		}

		if (this.todayButton) {
			this.todayButton.addEventListener(
				'click',
				this.resetDatePicker.bind(this)
			);
		}

		if (this.monthView) {
			this.checkButtonStatus();

			this.previousButton.addEventListener(
				'click',
				this.changeMonth.bind(this)
			);

			this.nextButton.addEventListener(
				'click',
				this.changeMonth.bind(this)
			);
		} else {
			this.previousButton.addEventListener(
				'click',
				this.togglePagination.bind(this)
			);

			this.nextButton.addEventListener(
				'click',
				this.togglePagination.bind(this)
			);
		}

		this.block = this.datePicker.closest('.acf-block');
		this.postsWrapper = this.block?.querySelector('.filters__post-wrapper');

		const pagination = this.block?.querySelector('.filter-pagination');

		if (pagination) {
			// paginate_links renders an anchor only when the page is navigable;
			// on the first/last page it's a disabled span, so check for a.prev/a.next.
			if (pagination.querySelector('a.prev')) {
				this.previousButton.removeAttribute('disabled');
			}

			if (pagination.querySelector('a.next')) {
				this.nextButton.removeAttribute('disabled');
			}
		}

		// Past mode is entered from the default list, so previous must be clickable
		// on page 1 even though there is no previous upcoming page.
		if (!this.monthView && 'true' === this.showPastEvents) {
			this.previousButton.removeAttribute('disabled');
		}

		window.addEventListener(
			'propel-filters-render-posts',
			this.checkButtonStatus.bind(this)
		);

		this.renderDatePicker();
	}

	renderDatePicker() {
		this.root.render(
			<DatePickerWrapper
				datePicker={this.datePicker}
				datePickerInput={this.datePickerInput}
				datePickerButton={this.datePickerButton}
				monthView={this.monthView}
				initialMonth={this.initialMonth}
				initialYear={this.initialYear}
				currentMonth={this.currentMonth}
				currentYear={this.currentYear}
				lastMonth={this.lastMonth}
				lastYear={this.lastYear}
				showPastEvents={this.showPastEvents}
			/>
		);
	}

	toggleDatePicker() {
		const toggleEvent = new CustomEvent('propel-toggle-date-picker');
		this.datePicker.dispatchEvent(toggleEvent);
	}

	resetDatePicker() {
		const resetEvent = new CustomEvent('propel-reset-date-picker');
		this.datePicker.dispatchEvent(resetEvent);
	}

	togglePagination(e) {
		e.currentTarget.setAttribute('disabled', 'disabled');

		let eventName = 'propel-filters-next-page';

		if (
			e.currentTarget.classList.contains(
				'filter-date__nav-button--previous'
			)
		) {
			eventName = 'propel-filters-previous-page';
		}

		const paginationEvent = new CustomEvent(eventName, {
			detail: {
				datePicker: this.datePicker,
				postsWrapper: this.postsWrapper,
			},
		});

		window.dispatchEvent(paginationEvent);
	}

	changeMonth(e) {
		let eventName = 'propel-filters-next-month';

		this.previousButton.setAttribute('disabled', 'disabled');
		this.nextButton.setAttribute('disabled', 'disabled');

		if (
			e.currentTarget.classList.contains(
				'filter-date__nav-button--previous'
			)
		) {
			eventName = 'propel-filters-previous-month';
		}

		const paginationEvent = new CustomEvent(eventName, {
			detail: {
				datePicker: this.datePicker,
			},
		});

		window.dispatchEvent(paginationEvent);
	}

	checkButtonStatus(e = null) {
		if (this.monthView) {
			// When past events are enabled, allow stepping into past months.
			if ('true' === this.showPastEvents) {
				this.previousButton.removeAttribute('disabled');
				this.nextButton.removeAttribute('disabled');

				return;
			}

			const previousYearButton = this.datePicker?.querySelector(
				'.filter-month-picker__nav-button'
			);
			const previousMonthButton = this.datePicker?.querySelector(
				'.filter-month-picker__button--active'
			)?.previousElementSibling;

			if (!previousYearButton && !previousMonthButton) {
				if (
					this.initialYear === this.currentYear &&
					this.initialMonth > this.currentMonth
				) {
					this.previousButton.removeAttribute('disabled');
				} else if (this.initialYear > this.currentYear) {
					this.previousButton.removeAttribute('disabled');
				} else {
					this.previousButton.setAttribute('disabled', 'disabled');
				}
			} else if (
				!previousYearButton?.disabled ||
				!previousMonthButton?.disabled
			) {
				this.previousButton.removeAttribute('disabled');
			} else {
				this.previousButton.setAttribute('disabled', 'disabled');
			}

			this.nextButton.removeAttribute('disabled');

			return;
		}

		const pastPage = e?.detail?.pastPage ?? 0;

		if (pastPage > 0) {
			// Past mode: previous goes deeper (disabled at the oldest event),
			// next always steps back toward today.
			if (pastPage < e?.detail?.maxPages) {
				this.previousButton.removeAttribute('disabled');
			} else {
				this.previousButton.setAttribute('disabled', 'disabled');
			}

			this.nextButton.removeAttribute('disabled');

			return;
		}

		// On upcoming page 1, previous is enabled only to cross into past mode.
		if (e?.detail?.currentPage > 1 || e?.detail?.canEnterPast) {
			this.previousButton.removeAttribute('disabled');
		} else {
			this.previousButton.setAttribute('disabled', 'disabled');
		}

		if (e?.detail?.currentPage < e?.detail?.maxPages) {
			this.nextButton.removeAttribute('disabled');
		} else {
			this.nextButton.setAttribute('disabled', 'disabled');
		}
	}
}

const eventDateFilters = document.querySelectorAll('.filter-date');

eventDateFilters.forEach((eventDateFilter) => {
	new DateFilter(eventDateFilter);
});

};
propelInit();
window.propel = window.propel || {};
window.propel.blocks = window.propel.blocks || {};
window.propel.blocks['filter-date'] = window.propel.blocks['filter-date'] || {};
window.propel.blocks['filter-date'].script = propelInit;
